/**
 * Scroll reveal — product grids + editorial About / CEO pages (block-by-block).
 */
const REVEAL_SELECTOR = [
  'main.content-for-layout > .shopify-section:not(.section-ysl-home-slider):not(.newsletter-popup-section):not(.cookie-banner-section)',
  '.product-grid__item',
  '.resource-list__item',
  '.recommendation-product-strip .resource-list__item',
].join(',');

const EDITORIAL_PAGES = ['ethos', 'about'];

const EDITORIAL_REVEAL_TARGETS = [
  '.text-block',
  '.slideshow-section',
  '.image-block',
  '.section-content-wrapper > a.button',
];

const EDITORIAL_REVEAL_SELECTOR = EDITORIAL_PAGES.flatMap((page) =>
  EDITORIAL_REVEAL_TARGETS.map((target) => `body.template-page-${page} ${target}`)
).join(',');

const GRID_CONTAINER_SELECTOR = '.product-grid, .resource-list, results-list';

const SKIP_SELECTOR =
  '[data-scroll-reveal="off"], .scroll-reveal--skip, dialog, [hidden], .visually-hidden';

const STAGGER_MS = 55;
const STAGGER_MAX = 8;
const EDITORIAL_STAGGER_MAX = 16;
const DEFAULT_EDITORIAL_STAGGER_MS = 55;
const DEFAULT_EDITORIAL_TIMING = 8;

/** @type {IntersectionObserver | null} */
let observer = null;

/** @type {HTMLElement[] | null} */
let editorialTargets = null;

/** @type {number} */
let scrollFrame = 0;

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getEditorialConfig() {
  const { dataset } = document.documentElement;

  return {
    enabled: dataset.editorialScrollReveal !== 'false',
    staggerMs: Number.parseInt(dataset.editorialScrollRevealStagger ?? `${DEFAULT_EDITORIAL_STAGGER_MS}`, 10),
    timing: Number.parseInt(dataset.editorialScrollRevealTiming ?? `${DEFAULT_EDITORIAL_TIMING}`, 10),
  };
}

function isEditorialRevealEnabled() {
  return getEditorialConfig().enabled;
}

function isEditorialPage() {
  return EDITORIAL_PAGES.some((page) => document.body.classList.contains(`template-page-${page}`));
}

function isSkipped(element) {
  return Boolean(element.closest(SKIP_SELECTOR));
}

function getViewportHeight() {
  return window.visualViewport?.height ?? window.innerHeight;
}

function getRevealTimingInset(isEditorialElement) {
  if (isEditorialElement) {
    return getEditorialConfig().timing;
  }

  return DEFAULT_EDITORIAL_TIMING;
}

function getStaggerIndex(element) {
  if (
    !element.matches(
      '.product-grid__item, .resource-list__item, .recommendation-product-strip .resource-list__item'
    )
  ) {
    return 0;
  }

  const parent = element.parentElement;
  if (!parent) return 0;

  const siblings = parent.querySelectorAll(
    '.product-grid__item, .resource-list__item, .recommendation-product-strip .resource-list__item'
  );

  let index = 0;
  for (let i = 0; i < siblings.length; i += 1) {
    if (siblings[i] === element) {
      index = i;
      break;
    }
  }

  return Math.min(index, STAGGER_MAX);
}

function refreshEditorialTargets(root = document) {
  if (!isEditorialPage() || !isEditorialRevealEnabled()) {
    editorialTargets = null;
    return;
  }

  editorialTargets = Array.from(root.querySelectorAll(EDITORIAL_REVEAL_SELECTOR)).filter(
    (element) => element instanceof HTMLElement && !isSkipped(element)
  );
}

function getEditorialStaggerIndex(element) {
  if (!editorialTargets) return 0;

  for (let i = 0; i < editorialTargets.length; i += 1) {
    if (editorialTargets[i] === element) {
      return Math.min(i, EDITORIAL_STAGGER_MAX);
    }
  }

  return 0;
}

function isInRevealZone(element) {
  const rect = element.getBoundingClientRect();
  const vh = getViewportHeight();
  const isEditorialElement = element.classList.contains('scroll-reveal--editorial');
  const inset = getRevealTimingInset(isEditorialElement);
  const bottomTrigger = vh * (0.98 - inset * 0.01);
  const topTrigger = vh * 0.04;

  return rect.top < bottomTrigger && rect.bottom > topTrigger && rect.height > 0;
}

function revealElement(element) {
  element.classList.add('is-revealed');
  observer?.unobserve(element);
}

function checkVisibleElements() {
  document.querySelectorAll('.scroll-reveal:not(.is-revealed)').forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    if (isInRevealZone(element)) {
      revealElement(element);
    }
  });
}

function scheduleVisibilityCheck() {
  cancelAnimationFrame(scrollFrame);
  scrollFrame = requestAnimationFrame(checkVisibleElements);
}

function prepareElement(element) {
  if (element.classList.contains('scroll-reveal')) return;
  if (isSkipped(element)) return;

  const editorial =
    isEditorialPage() &&
    isEditorialRevealEnabled() &&
    EDITORIAL_REVEAL_TARGETS.some((target) => element.matches(target));

  if (
    !editorial &&
    element.matches('main.content-for-layout > .shopify-section') &&
    element.querySelector(GRID_CONTAINER_SELECTOR)
  ) {
    return;
  }

  if (
    !editorial &&
    isEditorialPage() &&
    element.matches('main.content-for-layout > .shopify-section')
  ) {
    return;
  }

  const stagger = editorial ? getEditorialStaggerIndex(element) : getStaggerIndex(element);
  const editorialStaggerMs = getEditorialConfig().staggerMs;
  const delayMs = editorial ? stagger * editorialStaggerMs : stagger * STAGGER_MS;

  element.classList.add('scroll-reveal');
  if (editorial) {
    element.classList.add('scroll-reveal--editorial');
  }
  element.style.setProperty('--scroll-reveal-delay', `${delayMs}ms`);

  if (element.classList.contains('is-revealed')) return;
  observer?.observe(element);
}

function scan(root = document) {
  if (prefersReducedMotion()) return;

  refreshEditorialTargets(root);

  const selectors =
    isEditorialPage() && isEditorialRevealEnabled() ? EDITORIAL_REVEAL_SELECTOR : REVEAL_SELECTOR;

  root.querySelectorAll(selectors).forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    prepareElement(element);
  });

  scheduleVisibilityCheck();
}

function getObserverRootMargin() {
  const inset = isEditorialPage() ? getEditorialConfig().timing : DEFAULT_EDITORIAL_TIMING;
  return `0px 0px -${inset}% 0px`;
}

function initObserver() {
  if (observer) return;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (entry.intersectionRatio < 0.05) return;
        revealElement(entry.target);
      });
    },
    {
      root: null,
      rootMargin: getObserverRootMargin(),
      threshold: [0, 0.05, 0.12, 0.2],
    }
  );
}

function initScrollFallback() {
  window.addEventListener('scroll', scheduleVisibilityCheck, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleVisibilityCheck);
  window.addEventListener('resize', scheduleVisibilityCheck, { passive: true });
}

function initMutationObserver() {
  const main = document.querySelector('main.content-for-layout');
  if (!main) return;

  let frame = 0;
  const rescan = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => scan(main));
  };

  new MutationObserver(rescan).observe(main, {
    childList: true,
    subtree: true,
  });
}

function init() {
  if (prefersReducedMotion()) return;

  initObserver();
  initScrollFallback();
  scan();
  initMutationObserver();

  window.addEventListener('load', () => {
    scheduleVisibilityCheck();
    setTimeout(scheduleVisibilityCheck, 150);
  });

  document.addEventListener('shopify:section:load', () => scan());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
